"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var ImmutableTree_1 = require("./util/ImmutableTree");
var Path_1 = require("./util/Path");
var util_1 = require("@firebase/util");
var Node_1 = require("./snap/Node");
var PriorityIndex_1 = require("./snap/indexes/PriorityIndex");
var util_2 = require("@firebase/util");
/**
 * This class holds a collection of writes that can be applied to nodes in unison. It abstracts away the logic with
 * dealing with priority writes and multiple nested writes. At any given path there is only allowed to be one write
 * modifying that path. Any write to an existing path or shadowing an existing path will modify that existing write
 * to reflect the write added.
 *
 * @constructor
 * @param {!ImmutableTree.} writeTree
 */
var CompoundWrite = /** @class */ (function () {
 function CompoundWrite(writeTree_) {
 this.writeTree_ = writeTree_;
 }
 /**
 * @param {!Path} path
 * @param {!Node} node
 * @return {!CompoundWrite}
 */
 CompoundWrite.prototype.addWrite = function (path, node) {
 if (path.isEmpty()) {
 return new CompoundWrite(new ImmutableTree_1.ImmutableTree(node));
 }
 else {
 var rootmost = this.writeTree_.findRootMostValueAndPath(path);
 if (rootmost != null) {
 var rootMostPath = rootmost.path;
 var value = rootmost.value;
 var relativePath = Path_1.Path.relativePath(rootMostPath, path);
 value = value.updateChild(relativePath, node);
 return new CompoundWrite(this.writeTree_.set(rootMostPath, value));
 }
 else {
 var subtree = new ImmutableTree_1.ImmutableTree(node);
 var newWriteTree = this.writeTree_.setTree(path, subtree);
 return new CompoundWrite(newWriteTree);
 }
 }
 };
 /**
 * @param {!Path} path
 * @param {!Object.} updates
 * @return {!CompoundWrite}
 */
 CompoundWrite.prototype.addWrites = function (path, updates) {
 var newWrite = this;
 util_1.forEach(updates, function (childKey, node) {
 newWrite = newWrite.addWrite(path.child(childKey), node);
 });
 return newWrite;
 };
 /**
 * Will remove a write at the given path and deeper paths. This will not modify a write at a higher
 * location, which must be removed by calling this method with that path.
 *
 * @param {!Path} path The path at which a write and all deeper writes should be removed
 * @return {!CompoundWrite} The new CompoundWrite with the removed path
 */
 CompoundWrite.prototype.removeWrite = function (path) {
 if (path.isEmpty()) {
 return CompoundWrite.Empty;
 }
 else {
 var newWriteTree = this.writeTree_.setTree(path, ImmutableTree_1.ImmutableTree.Empty);
 return new CompoundWrite(newWriteTree);
 }
 };
 /**
 * Returns whether this CompoundWrite will fully overwrite a node at a given location and can therefore be
 * considered "complete".
 *
 * @param {!Path} path The path to check for
 * @return {boolean} Whether there is a complete write at that path
 */
 CompoundWrite.prototype.hasCompleteWrite = function (path) {
 return this.getCompleteNode(path) != null;
 };
 /**
 * Returns a node for a path if and only if the node is a "complete" overwrite at that path. This will not aggregate
 * writes from deeper paths, but will return child nodes from a more shallow path.
 *
 * @param {!Path} path The path to get a complete write
 * @return {?Node} The node if complete at that path, or null otherwise.
 */
 CompoundWrite.prototype.getCompleteNode = function (path) {
 var rootmost = this.writeTree_.findRootMostValueAndPath(path);
 if (rootmost != null) {
 return this.writeTree_
 .get(rootmost.path)
 .getChild(Path_1.Path.relativePath(rootmost.path, path));
 }
 else {
 return null;
 }
 };
 /**
 * Returns all children that are guaranteed to be a complete overwrite.
 *
 * @return {!Array.} A list of all complete children.
 */
 CompoundWrite.prototype.getCompleteChildren = function () {
 var children = [];
 var node = this.writeTree_.value;
 if (node != null) {
 // If it's a leaf node, it has no children; so nothing to do.
 if (!node.isLeafNode()) {
 node.forEachChild(PriorityIndex_1.PRIORITY_INDEX, function (childName, childNode) {
 children.push(new Node_1.NamedNode(childName, childNode));
 });
 }
 }
 else {
 this.writeTree_.children.inorderTraversal(function (childName, childTree) {
 if (childTree.value != null) {
 children.push(new Node_1.NamedNode(childName, childTree.value));
 }
 });
 }
 return children;
 };
 /**
 * @param {!Path} path
 * @return {!CompoundWrite}
 */
 CompoundWrite.prototype.childCompoundWrite = function (path) {
 if (path.isEmpty()) {
 return this;
 }
 else {
 var shadowingNode = this.getCompleteNode(path);
 if (shadowingNode != null) {
 return new CompoundWrite(new ImmutableTree_1.ImmutableTree(shadowingNode));
 }
 else {
 return new CompoundWrite(this.writeTree_.subtree(path));
 }
 }
 };
 /**
 * Returns true if this CompoundWrite is empty and therefore does not modify any nodes.
 * @return {boolean} Whether this CompoundWrite is empty
 */
 CompoundWrite.prototype.isEmpty = function () {
 return this.writeTree_.isEmpty();
 };
 /**
 * Applies this CompoundWrite to a node. The node is returned with all writes from this CompoundWrite applied to the
 * node
 * @param {!Node} node The node to apply this CompoundWrite to
 * @return {!Node} The node with all writes applied
 */
 CompoundWrite.prototype.apply = function (node) {
 return CompoundWrite.applySubtreeWrite_(Path_1.Path.Empty, this.writeTree_, node);
 };
 /**
 * @type {!CompoundWrite}
 */
 CompoundWrite.Empty = new CompoundWrite(new ImmutableTree_1.ImmutableTree(null));
 /**
 * @param {!Path} relativePath
 * @param {!ImmutableTree.} writeTree
 * @param {!Node} node
 * @return {!Node}
 * @private
 */
 CompoundWrite.applySubtreeWrite_ = function (relativePath, writeTree, node) {
 if (writeTree.value != null) {
 // Since there a write is always a leaf, we're done here
 return node.updateChild(relativePath, writeTree.value);
 }
 else {
 var priorityWrite_1 = null;
 writeTree.children.inorderTraversal(function (childKey, childTree) {
 if (childKey === '.priority') {
 // Apply priorities at the end so we don't update priorities for either empty nodes or forget
 // to apply priorities to empty nodes that are later filled
 util_2.assert(childTree.value !== null, 'Priority writes must always be leaf nodes');
 priorityWrite_1 = childTree.value;
 }
 else {
 node = CompoundWrite.applySubtreeWrite_(relativePath.child(childKey), childTree, node);
 }
 });
 // If there was a priority write, we only apply it if the node is not empty
 if (!node.getChild(relativePath).isEmpty() && priorityWrite_1 !== null) {
 node = node.updateChild(relativePath.child('.priority'), priorityWrite_1);
 }
 return node;
 }
 };
 return CompoundWrite;
}());
exports.CompoundWrite = CompoundWrite;

//# sourceMappingURL=CompoundWrite.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/CompoundWrite.js
// module id = 96
// module chunks = 0